import React from 'react';
import { getColor, getCycleColor } from './colorUtils';

interface BarChartBar {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  title?: string;
  bars: BarChartBar[];
  yAxisLabel?: string;
  showValues?: boolean;
  sorted?: boolean;
  showCumulativeLine?: boolean;
  maxValue?: number;
  unit?: string;
  height?: number;
}

/**
 * Compute nice Y-axis tick values that produce clean round numbers.
 * Returns an array of tick values from 0 up to the computed max.
 */
function computeYTicks(dataMax: number, desiredTicks: number = 5): number[] {
  if (dataMax <= 0) return [0];

  const rawStep = dataMax / desiredTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;

  let niceStep: number;
  if (residual <= 1.5) niceStep = 1 * magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMax = Math.ceil(dataMax / niceStep) * niceStep;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax; v += niceStep) {
    ticks.push(Math.round(v * 1e10) / 1e10);
  }
  return ticks;
}

/**
 * Format a numeric value for display: drop trailing decimals where possible.
 */
function formatValue(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  bars,
  yAxisLabel,
  showValues = true,
  sorted = false,
  showCumulativeLine = false,
  maxValue,
  unit = '',
  height = 300,
}) => {
  if (!bars || bars.length === 0) {
    return null;
  }

  // Sort if requested (Pareto: descending by value)
  const sortedBars = sorted
    ? [...bars].sort((a, b) => b.value - a.value)
    : bars;

  // Determine if any label is long enough to need rotation
  const hasLongLabels = sortedBars.some((b) => b.label.length > 6);

  // SVG viewBox dimensions
  const svgWidth = 600;
  const svgHeight = 400;

  // Chart area padding
  const padLeft = 60;
  const padRight = showCumulativeLine ? 60 : 40;
  const padTop = 30;
  const padBottom = hasLongLabels ? 80 : 60;

  const chartWidth = svgWidth - padLeft - padRight;
  const chartHeight = svgHeight - padTop - padBottom;

  // Y-axis scaling
  const dataMax = maxValue ?? Math.max(...sortedBars.map((b) => b.value));
  const yTicks = computeYTicks(dataMax);
  const yMax = yTicks[yTicks.length - 1];

  // Bar layout
  const barCount = sortedBars.length;
  const groupWidth = chartWidth / barCount;
  const barPadding = Math.max(groupWidth * 0.2, 4);
  const barWidth = groupWidth - barPadding * 2;

  // Cumulative percentages (for Pareto line)
  const total = sortedBars.reduce((sum, b) => sum + b.value, 0);
  let cumulative = 0;
  const cumulativePoints = sortedBars.map((b, i) => {
    cumulative += b.value;
    const pct = total > 0 ? (cumulative / total) * 100 : 0;
    const cx = padLeft + groupWidth * i + groupWidth / 2;
    const cy = padTop + chartHeight - (pct / 100) * chartHeight;
    return { pct, cx, cy };
  });

  // Right-axis ticks for cumulative % (0, 25, 50, 75, 100)
  const cumulativeTicks = [0, 25, 50, 75, 100];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
        </div>
      )}

      <div className="p-4">
        <svg
          width="100%"
          style={{ maxHeight: height + 'px' }}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={title || 'Bar chart'}
        >
          {/* Y-axis label */}
          {yAxisLabel && (
            <text
              x={14}
              y={padTop + chartHeight / 2}
              textAnchor="middle"
              dominantBaseline="central"
              transform={`rotate(-90, 14, ${padTop + chartHeight / 2})`}
              fontSize={11}
              fill="#6B7280"
            >
              {yAxisLabel}
            </text>
          )}

          {/* Y-axis ticks and grid lines */}
          {yTicks.map((tick) => {
            const y = padTop + chartHeight - (tick / yMax) * chartHeight;
            return (
              <g key={`ytick-${tick}`}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={padLeft + chartWidth}
                  y2={y}
                  stroke="#E5E7EB"
                  strokeWidth={1}
                  strokeDasharray={tick === 0 ? undefined : '4 3'}
                />
                <text
                  x={padLeft - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="central"
                  fontSize={11}
                  fill="#6B7280"
                >
                  {formatValue(tick)}
                  {unit && tick === yTicks[yTicks.length - 1] ? ` ${unit}` : ''}
                </text>
              </g>
            );
          })}

          {/* X-axis baseline */}
          <line
            x1={padLeft}
            y1={padTop + chartHeight}
            x2={padLeft + chartWidth}
            y2={padTop + chartHeight}
            stroke="#D1D5DB"
            strokeWidth={1}
          />

          {/* Y-axis vertical line */}
          <line
            x1={padLeft}
            y1={padTop}
            x2={padLeft}
            y2={padTop + chartHeight}
            stroke="#D1D5DB"
            strokeWidth={1}
          />

          {/* Bars */}
          {sortedBars.map((bar, i) => {
            const colorSet = bar.color
              ? getColor(bar.color)
              : getCycleColor(i);
            const barHeight =
              yMax > 0 ? (bar.value / yMax) * chartHeight : 0;
            const x = padLeft + groupWidth * i + barPadding;
            const y = padTop + chartHeight - barHeight;

            return (
              <g key={`bar-${i}`}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={colorSet.fill}
                  rx={4}
                  ry={4}
                />
                {/* Clip the bottom corners to be square by overlaying a small rect */}
                {barHeight > 8 && (
                  <rect
                    x={x}
                    y={y + barHeight - 4}
                    width={barWidth}
                    height={4}
                    fill={colorSet.fill}
                  />
                )}

                {/* Value label above bar */}
                {showValues && (
                  <text
                    x={x + barWidth / 2}
                    y={y - 6}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={600}
                    fill="#374151"
                  >
                    {formatValue(bar.value)}
                    {unit ? ` ${unit}` : ''}
                  </text>
                )}

                {/* X-axis label */}
                {hasLongLabels ? (
                  <text
                    x={x + barWidth / 2}
                    y={padTop + chartHeight + 12}
                    textAnchor="end"
                    dominantBaseline="central"
                    fontSize={10}
                    fill="#6B7280"
                    transform={`rotate(-45, ${x + barWidth / 2}, ${padTop + chartHeight + 12})`}
                  >
                    {bar.label}
                  </text>
                ) : (
                  <text
                    x={x + barWidth / 2}
                    y={padTop + chartHeight + 18}
                    textAnchor="middle"
                    fontSize={11}
                    fill="#6B7280"
                  >
                    {bar.label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Cumulative % line (Pareto overlay) */}
          {showCumulativeLine && total > 0 && (
            <>
              {/* Right Y-axis line */}
              <line
                x1={padLeft + chartWidth}
                y1={padTop}
                x2={padLeft + chartWidth}
                y2={padTop + chartHeight}
                stroke="#D1D5DB"
                strokeWidth={1}
              />

              {/* Right Y-axis ticks */}
              {cumulativeTicks.map((tick) => {
                const y =
                  padTop + chartHeight - (tick / 100) * chartHeight;
                return (
                  <text
                    key={`cum-tick-${tick}`}
                    x={padLeft + chartWidth + 8}
                    y={y}
                    textAnchor="start"
                    dominantBaseline="central"
                    fontSize={10}
                    fill="#9CA3AF"
                  >
                    {tick}%
                  </text>
                );
              })}

              {/* Cumulative polyline */}
              <polyline
                points={cumulativePoints
                  .map((p) => `${p.cx},${p.cy}`)
                  .join(' ')}
                fill="none"
                stroke="#EF4444"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Cumulative dots with percentage labels */}
              {cumulativePoints.map((p, i) => (
                <g key={`cum-dot-${i}`}>
                  <circle
                    cx={p.cx}
                    cy={p.cy}
                    r={4}
                    fill="#FFFFFF"
                    stroke="#EF4444"
                    strokeWidth={2}
                  />
                  <text
                    x={p.cx}
                    y={p.cy - 10}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight={600}
                    fill="#DC2626"
                  >
                    {Math.round(p.pct)}%
                  </text>
                </g>
              ))}
            </>
          )}
        </svg>
      </div>
    </div>
  );
};

export default BarChart;
